// ¿SE VE ESTE MÓDULO? — LA REGLA, ESCRITA UNA VEZ.
//
// EL INCIDENTE QUE LA TRAE (2-sep-2026, con las palabras del dueño):
//
//     «tengo un usuario con rol de recepción al que le di a su usuario acceso a "Almacén / stock"
//      […] sin embargo en el sidebar no le sale almacén […] le tuve que dar otro rol que sí tiene
//      acceso a Almacén para poder acceder y que trabaje»
//
// Lo que pasaba: el permiso entraba por un AND al FINAL de la decisión —`visible && permiso(clave)`—
// así que SOLO PODÍA QUITAR, NUNCA DAR. Quien decidía de verdad era la lista de roles clavada en la
// entrada del menú (`roles: ['admin','almacen']` en Almacén), y esa lista no la mueve conceder un
// permiso a UNA persona. El servidor SÍ abría (contestaba 200): la puerta estaba abierta y el botón
// escondido. Por eso hubo que cambiarle el rol a la recepcionista.
//
// ── LA REGLA ────────────────────────────────────────────────────────────────────────────────────
//   UN PERMISO **EXPLÍCITO** MANDA. Explícito = alguien lo ha decidido: una casilla marcada o
//   desmarcada para ESA PERSONA (`usuario_permisos`) o para SU ROL (`rol_permisos`, la matriz de
//   «Accesos por rol»). Conceder ABRE el módulo. Denegar lo CIERRA.
//
//   UN PERMISO **DE FÁBRICA** NO ABRE NADA POR SÍ MISMO. El `rolesDefault` del catálogo —la casilla
//   que viene marcada de serie y que nadie ha tocado— NO enseña un módulo nuevo: ahí sigue decidiendo
//   la lista `roles` de la entrada del menú, exactamente como hasta hoy. Sí sigue CERRANDO, porque
//   eso es lo que ya hacía el AND del final y quitarlo daría acceso a quien no lo tiene.
//
//   Un módulo SIN clave se decide por rol, exactamente como siempre.
//
// ── POR QUÉ LA MITAD DE FÁBRICA NO ABRE, QUE ES LA PARTE FINA ───────────────────────────────────
// La primera versión de este arreglo decía, a secas, «si hay clave manda el permiso». Es la regla
// buena y es a donde hay que llegar — pero aplicada HOY abría de golpe cinco módulos al rol ALMACÉN,
// entre ellos el WhatsApp de los pacientes. No por culpa de la regla: porque el catálogo de permisos
// trae marcadas por defecto casillas que en el menú son inertes (`R_GESTION_AUX` y
// `R_TODOS_MENOS_LIMP` incluyen 'almacen'), y en cuanto el permiso manda, esas casillas conceden de
// verdad. Nadie había decidido eso, y un despliegue no es el sitio donde se decide quién ve el
// WhatsApp de los pacientes.
//
// El dueño lo dijo el mismo día: «referente al rol ya te diré cómo poner por defecto cada rol». Así
// que los defaults NO SE TOCAN y esta regla distingue lo decidido de lo heredado. CUANDO ÉL REVISE
// LOS DEFAULTS, esto se simplifica solo: se borra `permisoExplicito`, la línea de `explicito` se
// queda en «manda el permiso» y el campo `_explicitas` del servidor sobra. Es un puente, no una capa
// nueva del modelo de permisos.
//
// ── POR QUÉ VIVE AQUÍ Y NO DENTRO DE Layout.jsx ─────────────────────────────────────────────────
// Porque la misma pregunta se hace en TRES sitios: el menú lateral (`components/Layout.jsx`),
// `hooks/useModuloVisible` (las pestañas de Seguimiento y el «Al entrar, llévame a…» de Mi perfil) y
// la puerta de ruta (`PermRoute`, en `App.jsx`). Las copias tenían el mismo AND y el mismo fallo.
// Escribir la marca `porPermiso: true` a mano módulo a módulo era la otra salida, y es la trampa de
// siempre: el módulo veintiuno nace roto y nadie se entera hasta que alguien no puede trabajar. La
// regla es UNA y está aquí.
//
// Módulo PURO a propósito (sin React, sin importar Layout): `useModuloVisible` ya importa de
// `Layout.jsx`, y si esto importara de allí se cerraría el ciclo.

// ¿Le toca por su rol? `roles` ausente = a todos. Se miran el rol propio y el rol DE SERIE del que
// hereda (los roles personalizados de la clínica heredan el acceso de su `base_rol`).
export function tocaPorRol(roles, rolUsuario, rolBase) {
  if (!roles) return true
  return roles.includes(rolUsuario) || roles.includes(rolBase)
}

// ¿ESTA CLAVE LA HA DECIDIDO ALGUIEN, o viene de fábrica? Lo contesta el SERVIDOR en `_explicitas`
// (backend/services/permisos.js, `permisosEfectivos`), que es donde están las dos tablas de
// overrides; aquí solo se lee. Deducirlo en el navegador —"¿el default de su rol dice otra cosa?"—
// obligaría a traerse el catálogo entero y a tener la misma decisión escrita en dos repositorios.
//
// Mientras la respuesta no ha llegado, `_explicitas` no está y esto contesta NO: o sea, manda la
// lista de roles. Es justo lo que hace falta para que nadie pierda su menú en cada F5.
export function permisoExplicito(perms, clave) {
  if (!clave) return false
  const ex = perms && perms._explicitas
  return Array.isArray(ex) && ex.includes(clave)
}

// ¿SE LE HA CONCEDIDO A ESTA PERSONA, en su ficha de permisos? (B-589, 17-sep-2026) Lo manda el servidor en
// `_de_la_persona` (backend/routes/permisos.js · GET /mias): las claves con fila suya concedida. Sin el campo (un
// servidor de antes, o los permisos sin llegar) contesta NO, y todo se decide como hasta hoy.
export function concedidoALaPersona(perms, clave) {
  if (!clave) return false
  const de = perms && perms._de_la_persona
  return Array.isArray(de) && de.includes(clave) && perms?.[clave] === true
}

// LA DECISIÓN. Devuelve true/false.
//
//   ruta            — la del módulo ('/stock'), que es como el admin guarda sus decisiones de menú
//   roles           — la lista de la entrada del menú: QUIÉN VE ESTE MÓDULO DE FÁBRICA
//   clave           — clave del catálogo de permisos, o null/undefined si el módulo no tiene
//   rolUsuario      — user.rol          rolBase — user.base_rol (o el `_base_rol` del servidor)
//   perms           — el cuerpo de GET /api/permisos/mias tal cual (mapa clave→bool + `_explicitas`)
//   overrideRol     — { ruta: visible } del admin en «Accesos por rol» (mostrar/ocultar)
export function decideVisibilidad({ ruta, roles, clave, rolUsuario, rolBase, perms, overrideRol }) {
  const porRol = tocaPorRol(roles, rolUsuario, rolBase)
  // UN PERMISO DENEGADO CIERRA, Y CIERRA POR ENCIMA DE TODO — el explícito y el de fábrica. Es lo
  // que ya hacía el AND del final y no se puede perder: si esa persona no tiene la clave, el
  // servidor le contestará 403, y un botón que lleva a un 403 es peor que no enseñar el botón. Se
  // comprueba con `=== false` (no "distinto de true") para que mientras los permisos cargan no
  // cierre nada: `undefined` es "todavía no lo sé", no "no".
  if (clave && perms?.[clave] === false) return false
  // LO QUE LA CLÍNICA LE HA DADO A ESTA PERSONA SALE, AUNQUE A SU ROL SE LE OCULTARA (B-589). El dueño: «si un usuario
  // tiene permiso para cobrar, que se marque automáticamente el acceso a esa pestaña». Con el «Ocultar» de su rol en
  // «Accesos por rol» mandando por encima de todo, conceder algo a UNA persona no le sacaba la pestaña: tenía el permiso
  // (y el servidor la dejaba entrar) y el botón no salía. Lo decidido para la persona es más concreto que lo decidido
  // para su rol, así que manda. Lo decidido para el ROL sigue igual: el mostrar/ocultar de abajo.
  if (concedidoALaPersona(perms, clave)) return true
  // Y AQUÍ ES DONDE ABRE, Y SOLO CUANDO ALGUIEN LO HA DECIDIDO. Con la clave concedida a esa persona
  // o a su rol, el botón sale aunque su rol no esté en la lista de arriba: ese es literalmente el
  // caso del encargo. Sin decisión, manda la lista — que es lo que se ve hoy, sin estrenar nada.
  const base = permisoExplicito(perms, clave) ? perms?.[clave] === true : porRol
  // El MOSTRAR/OCULTAR que el admin fijó para ese rol manda sobre lo anterior. (Mostrar concede
  // además la clave, ver backend/routes/rolModulos.js, así que no se contradice con el deny.)
  return (ruta in (overrideRol || {})) ? !!overrideRol[ruta] : base
}

export default decideVisibilidad
